import { localeComparator, sortBy, toggleElementsOnList } from '@fifa-bsm/bo-frontend-shared/utils';
import { isEmpty } from '@fifa-bsm/bo-frontend-shared/validators';
import { pipe } from '@fifa-bsm/bo-frontend-shared/utils';
import { groupBy } from '@fifa-bsm/bo-frontend-shared/utils';
import { SortDirection } from '@/app/shared/constants/sortDirection';

const initialState = () => ({
    active: false,
    selectedActiveItems: [],
    selectedEmptySeatIds: [],
    itemsToSwap: [],
});

const getters = ({ canPerform, canActivate }) => ({
    canPerform,
    canActivate,
    counts(state) {
        return {
            selected: state.selectedActiveItems.length,
            itemsToSwap: state.itemsToSwap.length,
            emptySwappableSeats: state.selectedEmptySeatIds.length,
            allSeatsSelectedForSwap: state.itemsToSwap.length + state.selectedEmptySeatIds.length,
        };
    },
});

const actions = ({ swapSeats, onActivate }) => ({
    swapSeats,
    activate(context, { initialActiveItems }) {
        context.commit('activate', initialActiveItems);
        onActivate?.(context);
    },
});

const mutations = {
    activate(state, initialActiveItems) {
        state.active = true;
        state.selectedActiveItems = initialActiveItems;
    },
    deactivate(state) {
        state.active = false;
        state.selectedActiveItems = [];
        state.selectedEmptySeatIds = [];
        state.itemsToSwap = [];
    },
    setSelectedEmptySeatIds(state, seatIds) {
        state.selectedEmptySeatIds = seatIds;
    },
    setSelectedActiveItems(state, activeItems) {
        state.selectedActiveItems = activeItems;
    },
    setItemsToSwap(state, items) {
        state.itemsToSwap = items;
    },
    toggleItemToSwap(state, item) {
        state.itemsToSwap = toggleElementsOnList(state.itemsToSwap, [item]);
    },
};

export const store = {
    state: initialState,
    getters,
    actions,
    mutations,
};

export const assignSwappableSeats = ({
    itemsToSwapByRowId,
    swappableSeatsByRowId,
    alreadyAssignedSeatIds = [],
    discardCallback = () => false,
}) => {
    const assignedSeatIds = [...alreadyAssignedSeatIds];
    const itemsNotFittingInRow = [];

    let assignments = Object.entries(itemsToSwapByRowId).reduce((acc, [rowId, items]) => {
        items.forEach((item, index) => {
            if (isEmpty(item) || (discardCallback && discardCallback(item))) {
                return;
            }

            if (
                swappableSeatsByRowId[rowId]?.[index] &&
                !assignedSeatIds.includes(swappableSeatsByRowId[rowId][index].id)
            ) {
                acc.push({ item, seat: swappableSeatsByRowId[rowId][index] });
                assignedSeatIds.push(swappableSeatsByRowId[rowId][index].id);
            } else {
                itemsNotFittingInRow.push(item);
            }
        });

        return acc;
    }, []);

    const flattenedSwappableSeats = Object.values(swappableSeatsByRowId).flat();

    if (itemsNotFittingInRow.length > 0) {
        assignments = [
            ...assignments,
            ...itemsNotFittingInRow.map((item) => {
                const seat = findFirstFreeSeat(item, flattenedSwappableSeats, assignedSeatIds);
                assignedSeatIds.push(seat.id);
                return { item, seat, isFirstFreeSeat: true };
            }),
        ];
    }

    return assignments;
};

const findFirstFreeSeat = (item, swappableSeats, assignedSeatIds) => {
    const firstFreeSeatInRow = swappableSeats.find(
        (seat) => seat.row.id === item.seat.row.id && !assignedSeatIds.includes(seat.id)
    );
    const firstFreeSeat = swappableSeats.find((seat) => !assignedSeatIds.includes(seat.id));
    return firstFreeSeatInRow ?? firstFreeSeat;
};

export const mapIntoSeatsAndSortByName = (seatsById) =>
    pipe(
        (seatIds) => seatIds.map((seatId) => seatsById[seatId]),
        (seats) =>
            sortBy(
                localeComparator((seat) => seat.name),
                seats
            )
    );

export const toSeatIds = (items) => items.map((item) => item.seat.id);

export const buildDataForBulkSwap = ({
    selectedActiveItems,
    itemsToSwap,
    selectedEmptySeatIds,
    seatsMap,
}) => {
    const activeItemsByRowId = pipe(
        (items) =>
            sortBy(
                localeComparator((ticket) => ticket.seat.name, SortDirection.ASC, {
                    numeric: true,
                    sensitivity: 'base',
                }),
                items
            ),
        groupBy((ticket) => ticket.seat.row.id)
    )(selectedActiveItems);

    const swappableSeats = mapIntoSeatsAndSortByName(seatsMap)([
        ...selectedEmptySeatIds,
        ...toSeatIds(itemsToSwap),
    ]);

    const swappableSeatsByRowId = groupBy((seat) => seat.row.id)(swappableSeats);

    const activeItemsSeatsByRowId = pipe(
        toSeatIds,
        mapIntoSeatsAndSortByName(seatsMap),
        groupBy((seat) => seat.row.id)
    )(selectedActiveItems);

    const itemsToSwapByRowId = Object.entries(swappableSeatsByRowId).reduce(
        (acc, [rowId, seats]) => {
            acc[rowId] = seats.map((seat) => {
                return itemsToSwap.find((item) => item.seat?.id === seat.id);
            });

            return acc;
        },
        {}
    );

    return {
        activeItemsByRowId,
        swappableSeats,
        swappableSeatsByRowId,
        activeItemsSeatsByRowId,
        itemsToSwapByRowId,
    };
};
